// Best-effort browser + OS detection from navigator.userAgent for the
// io_devices.name field. Goal: produce a label that's recognizable in
// the device list when a user has signed in from multiple browsers
// (e.g. "Chrome on macOS" + "Safari on iOS" + "Firefox on Linux"), so
// the auto-generated name isn't "Browser (abc12345)" three times.
//
// User-agent parsing is famously fragile — every UA pretends to be
// every other UA for legacy compat. This is intentionally simplistic:
// a few keyword checks for the common cases, falls back to "Browser"
// for anything we don't recognize. The user can always rename via the
// device-list UI; this just provides a sensible default.

function detectOS(ua: string): string | null {
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return null
}

function detectBrowser(ua: string): string | null {
  // Edge before Chrome — Edge's UA includes "Chrome".
  if (/Edg\//.test(ua)) return 'Edge'
  // Opera before Chrome — Opera's UA includes "Chrome".
  if (/OPR\/|Opera/.test(ua)) return 'Opera'
  // Firefox is distinct.
  if (/Firefox/.test(ua)) return 'Firefox'
  // Chrome before Safari — Chrome's UA includes "Safari".
  if (/Chrome\//.test(ua)) return 'Chrome'
  // Safari last (anything that says Safari but isn't one of the above).
  if (/Safari/.test(ua)) return 'Safari'
  return null
}

/// Produces a human-friendly label like "Chrome on macOS". Falls back to
/// "Browser" when the user agent doesn't parse into a known browser/OS
/// pair. The returned string is suitable as an io_devices.name; uniqueness
/// per (owner, name) is enforced upstream by suffixing with a slice of the
/// io_device_id.
export function browserDeviceLabel(ua: string = navigator.userAgent): string {
  const os = detectOS(ua)
  const browser = detectBrowser(ua)
  if (browser && os) return `${browser} on ${os}`
  if (browser) return browser
  if (os) return `Browser on ${os}`
  return 'Browser'
}
